/**
 * How long ago something happened, in the shortest phrase that is still honest.
 *
 * Used for sync freshness, where the point is to let someone judge whether a
 * number is worth trusting rather than to report a precise duration.
 */
export const timeAgo = (value, now = new Date()) => {
  if (!value) return null;

  const then = new Date(value);
  if (Number.isNaN(then.getTime())) return null;

  const seconds = Math.max(0, Math.round((now - then) / 1000));

  if (seconds < 45) return 'just now';
  if (seconds < 90) return 'a minute ago';

  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `${minutes} minutes ago`;

  const hours = Math.round(minutes / 60);
  if (hours === 1) return 'an hour ago';
  if (hours < 24) return `${hours} hours ago`;

  const days = Math.round(hours / 24);
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days} days ago`;

  const months = Math.round(days / 30);
  return months === 1 ? 'a month ago' : `${months} months ago`;
};

// Sync runs every half hour by default, so a couple of hours without one means
// something is wrong with the connection rather than merely quiet.
const STALE_AFTER_MS = 2 * 60 * 60 * 1000;

/**
 * Whether data from this moment should be presented with a warning.
 * Never synced counts as stale: an empty hill and an unsynced hill look alike.
 */
export const isStale = (value, now = new Date()) => {
  if (!value) return true;
  const then = new Date(value);
  if (Number.isNaN(then.getTime())) return true;
  return now - then > STALE_AFTER_MS;
};

/**
 * A day, written the way someone would say it out loud.
 */
export const shortDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};
